import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { GripVertical, Trash2, Asterisk } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FormField {
  id: string;
  type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'number' | 'date' | 'file';
  label: string;
  placeholder: string;
  required: boolean;
  options?: string[];
}

interface DraggableFormFieldProps {
  id: string;
  field: FormField;
  onUpdate: (updates: Partial<FormField>) => void;
  onRemove: () => void;
}

export const DraggableFormField = ({
  id,
  field,
  onUpdate,
  onRemove,
}: DraggableFormFieldProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 0,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative animate-in fade-in slide-in-from-bottom-4 duration-500",
        isDragging ? "opacity-30 scale-95" : "opacity-100"
      )}
    >
      {/* Precision Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="absolute -left-12 top-1/2 -translate-y-1/2 p-3 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-all hidden lg:block hover:bg-surface-high rounded-xl"
      >
        <GripVertical className="h-5 w-5 text-primary/40 group-hover:text-primary transition-colors" />
      </div>

      <Card className={cn(
        "border-none bg-surface-low/80 backdrop-blur-xl transition-all duration-500 rounded-[2rem] overflow-hidden",
        isDragging ? "shadow-[0_0_50px_rgba(0,218,243,0.1)] ring-1 ring-primary/20" : "shadow-xl shadow-black/20 hover:shadow-primary/5 hover:bg-surface-low"
      )}>
        <CardContent className="p-10">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                 <span className="text-[10px] font-bold text-primary uppercase tracking-widest">{field.type}</span>
                 <Label className="text-xl font-display font-bold tracking-tight flex items-center gap-2">
                   {field.label || 'Untitled Field'}
                   {field.required && <Asterisk className="h-4 w-4 text-primary" />}
                 </Label>
              </div>
              
              <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                 <button 
                   onClick={(e) => { e.stopPropagation(); onRemove(); }}
                   className="p-3 rounded-2xl hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                   title="Remove Field"
                 >
                    <Trash2 className="h-5 w-5" />
                 </button>
              </div>
            </div>

            {/* Field Preview Overlay */}
            <div className="relative">
              <div className="absolute inset-0 bg-transparent z-10" /> {/* Click Shield */}
              
              {field.type === 'textarea' ? (
                <div className="bg-surface-lowest/50 rounded-2xl p-6 min-h-[120px] text-muted-foreground/40 text-sm border-b-2 border-border/5">
                   {field.placeholder || 'Type here...'}
                </div>
              ) : field.type === 'select' || field.type === 'radio' || field.type === 'checkbox' ? (
                <div className="space-y-4 py-2">
                   {field.options?.slice(0, 3).map((opt, i) => (
                     <div key={i} className="flex items-center gap-4 group/opt">
                        <div className={cn(
                          "w-5 h-5 border-2 border-border/20 transition-all",
                          field.type === 'radio' ? "rounded-full" : "rounded-lg",
                          i === 0 ? "border-primary/50 bg-primary/10" : ""
                        )} />
                        <span className={cn(
                          "text-sm transition-colors",
                          i === 0 ? "text-foreground font-medium" : "text-muted-foreground"
                        )}>{opt}</span>
                     </div>
                   ))}
                   {(field.options?.length || 0) > 3 && (
                     <p className="text-[10px] text-primary font-bold uppercase tracking-widest pl-9">
                        + {(field.options?.length || 0) - 3} more options
                     </p>
                   )}
                </div>
              ) : (
                <div className="bg-surface-lowest/50 rounded-2xl px-6 py-4 text-muted-foreground/40 text-sm border-b-2 border-border/5">
                   {field.placeholder || 'Enter value...'}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tonal Accent (Digital Architect Instruction) */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-primary/20 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
};
